import { useState, useEffect, useMemo } from "react";
import { ActivityStatus } from "./types";
import type { ActivitySource, ActivityViewData } from "./types";

/**
 * Subscribes to one or more ActivitySource instances and merges their
 * outputs into a single, chronologically sorted list.
 *
 * Designed so the UI can combine different backends (Firestore tasks,
 * PubSub events, etc.) without knowing their internals. The hook waits
 * for every source to emit at least once before clearing the loading state.
 *
 * @returns activities - merged list sorted newest-first
 * @returns activeCount - number of pending/running items (drives the badge counter)
 * @returns isLoading - true until every source has emitted at least once
 */
export function useActivities(sources: ActivitySource[]) {
    const [activitiesBySrc, setActivitiesBySrc] = useState<ActivityViewData[][]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (sources.length === 0) {
            setActivitiesBySrc([]);
            setIsLoading(false);
            return;
        }

        setActivitiesBySrc([]);
        setIsLoading(true);

        const results: ActivityViewData[][] = new Array(sources.length).fill([]);
        const hasEmitted = new Array(sources.length).fill(false);

        const unsubscribes = sources.map((source, i) =>
            source.subscribe((activities) => {
                results[i] = activities;
                hasEmitted[i] = true;
                if (hasEmitted.every(Boolean)) setIsLoading(false);
                setActivitiesBySrc([...results]);
            })
        );

        return () => unsubscribes.forEach((unsub) => unsub());
    }, [sources]);

    const activities = useMemo(
        () => activitiesBySrc.flat().sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
        [activitiesBySrc]
    );

    const activeCount = useMemo(
        () =>
            activities.filter(
                (a) => a.status === ActivityStatus.Pending || a.status === ActivityStatus.Running
            ).length,
        [activities]
    );

    return { activities, activeCount, isLoading };
}
